import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth, useUser } from "@clerk/clerk-react";
import { CheckCircle, Sparkles, ArrowRight, Crown, Zap, Diamond } from "lucide-react";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const SubscriptionSuccessPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { getToken, isSignedIn } = useAuth();
    const { user } = useUser();
    const [subscriptionDetails, setSubscriptionDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const checkoutId = searchParams.get("checkout_id");

    // Plan icons and colors
    const planStyles = {
        economy: {
            icon: Zap,
            gradient: "from-emerald-500 to-teal-600",
            glow: "rgba(16, 185, 129, 0.3)",
        },
        premium: {
            icon: Crown,
            gradient: "from-[#FDB813] via-[#D4AF37] to-[#B8860B]",
            glow: "rgba(212, 175, 55, 0.5)",
        },
        pro: {
            icon: Diamond,
            gradient: "from-violet-500 to-purple-600",
            glow: "rgba(139, 92, 246, 0.3)",
        },
    };

    useEffect(() => {
        const fetchSubscriptionDetails = async () => {
            if (!isSignedIn || !checkoutId) {
                setLoading(false);
                return;
            }

            try {
                const token = await getToken();
                const response = await fetch(`${BACKEND_URL}/api/subscription/verify?checkout_id=${checkoutId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setSubscriptionDetails(data);
                } else {
                    // Even if verification fails, show a success message
                    // The webhook will handle actual subscription activation
                    setSubscriptionDetails({
                        plan: "premium",
                        status: "active",
                        message: "Your subscription is being activated..."
                    });
                }
            } catch (err) {
                console.error("Failed to verify subscription:", err);
                // Still show success - webhook handles actual activation
                setSubscriptionDetails({
                    plan: "premium",
                    status: "pending",
                    message: "Your subscription is being processed..."
                });
            } finally {
                setLoading(false);
            }
        };

        // Small delay to allow webhook to process
        const timer = setTimeout(fetchSubscriptionDetails, 1500);
        return () => clearTimeout(timer);
    }, [isSignedIn, checkoutId, getToken]);

    const planName = subscriptionDetails?.plan?.toLowerCase() || "premium";
    const planStyle = planStyles[planName] || planStyles.premium;
    const PlanIcon = planStyle.icon;

    if (loading) {
        return (
            <div className="min-h-screen bg-obsidian-300 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-6 relative">
                        <div className="absolute inset-0 border-4 border-gold-500/30 rounded-full" />
                        <div className="absolute inset-0 border-4 border-transparent border-t-gold-500 rounded-full animate-spin" />
                    </div>
                    <p className="text-zinc-400 animate-pulse">Confirming your subscription...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-obsidian-300 flex items-center justify-center p-4">
            {/* Animated background elements */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div
                    className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 animate-pulse"
                    style={{ background: `radial-gradient(circle, ${planStyle.glow} 0%, transparent 70%)` }}
                />
                <div
                    className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 animate-pulse"
                    style={{ background: `radial-gradient(circle, ${planStyle.glow} 0%, transparent 70%)`, animationDelay: '1s' }}
                />
            </div>

            {/* Success Card */}
            <div className="relative z-10 max-w-lg w-full">
                <div className="bg-obsidian-200/80 backdrop-blur-xl border border-gold-500/20 rounded-3xl p-8 md:p-12 text-center">

                    {/* Success Icon with Animation */}
                    <div className="relative mb-8">
                        <div
                            className={`w-24 h-24 mx-auto rounded-full bg-gradient-to-br ${planStyle.gradient} flex items-center justify-center`}
                            style={{ boxShadow: `0 0 60px ${planStyle.glow}` }}
                        >
                            <CheckCircle className="w-12 h-12 text-white" strokeWidth={2.5} />
                        </div>

                        {/* Sparkles animation */}
                        <Sparkles
                            className="absolute -top-2 -right-2 w-8 h-8 text-gold-400 animate-bounce"
                            style={{ animationDelay: '0.2s' }}
                        />
                        <Sparkles
                            className="absolute -bottom-1 -left-3 w-6 h-6 text-gold-500 animate-bounce"
                            style={{ animationDelay: '0.5s' }}
                        />
                    </div>

                    {/* Thank You Message */}
                    <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        Thank You! 🎉
                    </h1>

                    <p className="text-lg text-zinc-300 mb-8">
                        Your subscription has been successfully activated
                    </p>

                    {/* Plan Details Card */}
                    <div className="bg-obsidian-300/50 border border-gold-500/10 rounded-2xl p-6 mb-8">
                        <div className="flex items-center justify-center gap-3 mb-4">
                            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${planStyle.gradient} flex items-center justify-center`}>
                                <PlanIcon className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-xl font-semibold text-white capitalize">
                                {planName} Plan
                            </span>
                        </div>

                        <div className="space-y-3 text-left">
                            <div className="flex items-center justify-between py-2 border-b border-zinc-700/50">
                                <span className="text-zinc-400">Status</span>
                                <span className="flex items-center gap-2 text-emerald-400 font-medium">
                                    <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                                    Active
                                </span>
                            </div>

                            {user && (
                                <div className="flex items-center justify-between py-2 border-b border-zinc-700/50">
                                    <span className="text-zinc-400">Account</span>
                                    <span className="text-white">{user.primaryEmailAddress?.emailAddress}</span>
                                </div>
                            )}

                            {checkoutId && (
                                <div className="flex items-center justify-between py-2">
                                    <span className="text-zinc-400">Checkout ID</span>
                                    <span className="text-zinc-500 text-sm font-mono">{checkoutId.slice(0, 12)}...</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* What's Next Section */}
                    <div className="text-left mb-8">
                        <h3 className="text-lg font-semibold text-white mb-3">What's Next?</h3>
                        <ul className="space-y-2 text-zinc-400">
                            <li className="flex items-start gap-2">
                                <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                                <span>Your analysis limits have been upgraded</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                                <span>Premium features are now unlocked</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                                <span>Start analyzing with enhanced capabilities</span>
                            </li>
                        </ul>
                    </div>

                    {/* CTA Button */}
                    <button
                        onClick={() => navigate("/")}
                        className={`w-full py-4 px-6 rounded-xl bg-gradient-to-r ${planStyle.gradient} text-white font-semibold text-lg flex items-center justify-center gap-2 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg`}
                        style={{ boxShadow: `0 10px 40px ${planStyle.glow}` }}
                    >
                        Start Analyzing
                        <ArrowRight className="w-5 h-5" />
                    </button>

                    {/* Support Link */}
                    <p className="mt-6 text-sm text-zinc-500">
                        Need help?{" "}
                        <a href="mailto:support@example.com" className="text-gold-400 hover:text-gold-300 transition-colors">
                            Contact Support
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionSuccessPage;
